import { getJDUrlWithDomain } from "@/utils/jobs";
import { JobCardNew, Pagination } from "@monsterindia/global-ui-components";
import dynamic from "next/dynamic";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { fetchUserJobInfoAPI } from "@/components/dashboard/query";

import noResultData from "../../../noresult/utils/noResultData.json";
import Skeleton from "../../appliedJobCard/skeleton";
import { jobSaveUnsaveAPI } from "../query";

const ApplyJob = dynamic<{
  jobData: jobDataType;
  siteProperties: sitePropsType | undefined;
  pageName: string;
  btnClass: string;
  isLoggedIn: boolean;
  isMobile: boolean;
  isJobApplied: boolean;
}>(() => import("@monsterindia/seeker-jobs").then((mod) => mod.ApplyJob), {
  ssr: false,
});

const NoResults = dynamic(() => import("../../../noresult"), {
  ssr: false,
});

interface SimilarJobsProps {
  host: string;
  data: any;
  isMobile: boolean;
  similarJobsSelected: boolean;
  jobDescLoader: boolean;
  siteProps: any;
  sitePropsProperties: sitePropsType | undefined;
}

const SimilarJobs: React.FC<SimilarJobsProps> = ({
  host,
  data,
  isMobile,
  similarJobsSelected,
  jobDescLoader,
  siteProps,
  sitePropsProperties,
}) => {
  const [page, setPage] = useState(1);
  const [maxPageNum, setMaxPageNum] = useState(0);
  const [similarJobs, setSimilarJobs] = useState<any>(undefined);
  const [loader, setLoader] = useState(true);
  const [userInfo, setUserInfo] = useState<any>({
    appliedJobIds: [],
    savedJobIds: [],
  });
  const [savedJobs, setSavedJobs] = useState<number[]>([]);
  const similarJobsRef = useRef<HTMLDivElement>(null);
  const limitSimilarJobs = isMobile ? 10 : 6;

  const saveUnsaveJobHandler = async (jobId: number) => {
    const response: any = await jobSaveUnsaveAPI(
      jobId,
      savedJobs.includes(jobId) ? "unsave" : "save",
    );
    const responseMessage = response?.successMessage;
    if (responseMessage == "Job saved successfully") {
      setSavedJobs([...savedJobs, jobId]);
    } else if (responseMessage == "Job unsaved successfully") {
      setSavedJobs((prevData: number[]) => {
        return prevData.filter((prevJobId: number) => prevJobId !== jobId);
      });
    } else {
      console.log("Error in saving/unsaving job");
    }
  };

  const jobUserInfoHandler = useCallback(
    async (similarJobs: any) => {
      const jobDataIds = similarJobs.map((job: jobDataType) => job.id);
      const response = await fetchUserJobInfoAPI(jobDataIds);
      setUserInfo(response);
    },
    [isMobile],
  );

  useEffect(() => {
    setLoader(true);
    if (data) {
      const newSimilarJobs = data?.data?.filter(
        (data: any, index: number) => index < limitSimilarJobs,
      );
      setSimilarJobs(newSimilarJobs);
      setMaxPageNum(Math.ceil(data?.data?.length / limitSimilarJobs));
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      const newSimilarJobs = data?.data?.filter((data: any, index: number) =>
        isMobile
          ? index < page * limitSimilarJobs
          : index < page * limitSimilarJobs &&
            index >= (page - 1) * limitSimilarJobs,
      );
      setSimilarJobs(newSimilarJobs);
      jobUserInfoHandler(newSimilarJobs);
    }
  }, [page]);

  useEffect(() => {
    const modalRef = document.querySelector(".guic-modal-content");
    if (similarJobs == undefined) {
      if (modalRef) {
        modalRef?.classList.remove("overflow-y-scroll");
        modalRef?.classList.add("overflow-y-hidden");
      }
    } else {
      setTimeout(() => {
        setLoader(false);
        if (modalRef) {
          modalRef.classList.remove("overflow-y-hidden");
          modalRef.classList.add("overflow-y-scroll");
        }
      }, 200);
    }

    if (similarJobsRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (
              similarJobs.length < limitSimilarJobs ||
              data?.data?.length == similarJobs.length
            )
              return;
            setLoader(true);
            setPage((prev) => prev + 1);
            modalRef?.classList.remove("overflow-y-scroll");
            modalRef?.classList.add("overflow-y-hidden");
          }
        },
        {
          root: modalRef,
          threshold: 0.9,
        },
      );

      observer.observe(similarJobsRef.current);

      return () => observer.disconnect();
    }
    return () => {};
  }, [similarJobs]);

  return (
    <div
      className={`md:!border-border-subtle mx-auto max-w-screen-lg p-4 md:!rounded-2xl md:!rounded-t-none md:!border-t-0 md:!px-6 ${isMobile ? "!-ml-4 !-mr-4 !pb-10" : ""} ${similarJobsSelected ? "!pt-0" : ""}`}
      id="similar-jobs"
    >
      <div className={`flex flex-col gap-4 md:!gap-6`}>
        {!similarJobsSelected ? (
          <div className="text-[16px] font-bold leading-[24px] text-[#1D1934]">
            <p>Similar Jobs</p>
          </div>
        ) : null}

        <div
          className={`${!isMobile ? `grid grid-cols-2 grid-rows-${Math.ceil(similarJobs?.length / (2 * page)) || 3}` : "flex flex-col gap-4"} gap-[24px]`}
        >
          {(isMobile && similarJobs?.length > 0) || !isMobile ? (
            similarJobs?.map((data: any, index: number) => {
              return (
                <>
                  <div
                    ref={
                      index === similarJobs.length - 1 && isMobile
                        ? similarJobsRef
                        : null
                    }
                    className={`${!loader ? "" : "hidden"}`}
                  >
                    <JobCardNew
                      key={data?.company?.companyId || index}
                      applyClickHandler={() => {}}
                      cardClickHandler={(url: string, event: any) => {
                        const a = document.createElement("a");
                        a.href = url;
                        a.target = "_self";
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);

                        //when iframe will be removed
                        // router.push(url);
                      }}
                      isMobile
                      jobData={{
                        accountId: data?.accountId || "",
                        activeJob: data?.activeJob,
                        autoMatch: data?.autoMatch || 0,
                        channelId: data?.channelId,
                        channelName: data?.channelName || "",
                        closedAt: data?.closedAt,
                        company: {
                          companyId: data?.company?.companyId || "",
                          name: data?.company?.name || "",
                        },
                        companyId: data?.company?.companyId,
                        companyName: data?.company?.name || "",
                        createdAt: data?.createdAt,
                        currencyCode: data?.currencyCode,
                        description: data?.description || "",
                        designations:
                          (data?.designations &&
                            Array.isArray(data.designations) && [
                              ...data.designations,
                            ]) ||
                          [],
                        earlyApplicant: data?.earlyApplicant,
                        employerTypes:
                          (data?.employerTypes &&
                            Array.isArray(data.employerTypes) && [
                              ...data.employerTypes,
                            ]) ||
                          [],
                        employmentTypes:
                          (data?.employmentTypes &&
                            Array.isArray(data.employmentTypes) && [
                              ...data.employmentTypes,
                            ]) ||
                          [],
                        experience:
                          data.minimumExperience?.years &&
                          data.maximumExperience?.years &&
                          `${data.minimumExperience.years} - ${data.maximumExperience.years} years`,
                        freshness: data?.freshness,
                        functions:
                          (data?.functions &&
                            Array.isArray(data.functions) && [
                              ...data.functions,
                            ]) ||
                          [],
                        graceJob: data?.graceJob,
                        hideCompanyName: data?.hideCompanyName,
                        hideSalary: data?.hideSalary,
                        id: data?.id || "",
                        industries:
                          (data?.industries &&
                            Array.isArray(data.industries) && [
                              ...data.industries,
                            ]) ||
                          [],
                        isBold: data?.isBold,
                        isCJT: data?.isCJT,
                        isGptwCompany: data?.isGptwCompany,
                        isJdLogo: data?.isJDLogo,
                        isMicrosite: data?.isMicrosite,
                        isSearchLogo: data?.isSearchLogo || "",
                        isoCode: data?.isoCode || "en",
                        jdUrl: getJDUrlWithDomain(data?.jdUrl || "", host),
                        jobFrom: data?.jobFrom || "",
                        jobId: data?.jobId,
                        jobSalaryConfidential: data?.jobSalaryConfidential,
                        jobTypes:
                          (data?.jobTypes &&
                            Array.isArray(data.jobTypes) && [
                              ...data.jobTypes,
                            ]) ||
                          [],
                        kiwiCompanyId: data?.kiwiCompanyId || "",
                        kiwiJobId: data?.kiwiJobId || "",
                        kiwiRecruiterId: data?.kiwiRecruiterId || "",
                        locations:
                          data?.locations &&
                          Array.isArray(data.locations) &&
                          data.locations
                            .map((data: any) => data?.city || "")
                            .join(","),
                        maximumExperience: data?.maximumExperience &&
                          typeof data.maximumExperience == "object" && {
                            ...data.maximumExperience,
                          },
                        maximumExperienceFilter: data?.maximumExperienceFilter,
                        maximumSalary: data?.maximumSalary &&
                          typeof data.maximumSalary == "object" && {
                            ...data.maximumSalary,
                          },
                        maximumSalaryFilter: data?.maximumSalaryFilter,
                        maximumSalaryINRFilter: data?.maximumSalaryINRFilter,
                        maximumSalaryINRMonthlyFilter:
                          data?.maximumSalaryINRMonthlyFilter,
                        minimumExperience: data?.minimumExperience &&
                          typeof data.minimumExperience == "object" && {
                            ...data.minimumExperience,
                          },
                        minimumExperienceFilter: data?.minimumExperienceFilter,
                        minimumSalary: data?.minimumSalary &&
                          typeof data.minimumSalary == "object" && {
                            ...data.minimumSalary,
                          },
                        minimumSalaryFilter: data?.minimumSalaryFilter,
                        minimumSalaryINRFilter: data?.minimumSalaryINRFilter,
                        minimumSalaryINRMonthlyFilter:
                          data?.minimumSalaryINRMonthlyFilter,
                        postOnPublic: data?.postOnPublic,
                        postedAt: data?.postedAt,
                        postingBoard: data?.postingBoard || "",
                        questionnaire: data?.questionnaire,
                        quickApplyJob: data.quickApplyJob,
                        quickJob: data?.quickJob,
                        recruiterId: data?.recruiterId,
                        recruiterName: data?.recruiterName || "",
                        redirectStage: data?.redirectStage,
                        redirectUrl: data?.redirectUrl || "",
                        renewalAt: data.renewalAt,
                        salary:
                          data?.minimumSalaryINRFilter &&
                          data?.maximumSalaryINRFilter &&
                          `${data.minimumSalaryINRFilter} - ${data.maximumSalaryINRFilter}`,
                        score: data?.score,
                        showContactDetails: data?.showContactDetails,
                        siteContext: data?.siteContext || "rexmonster",
                        skills:
                          (data?.skills &&
                            Array.isArray(data.skills) && [
                              ...data.skills.map(
                                (data: any) => data?.text || "",
                              ),
                            ]) ||
                          [],
                        status: data?.status,
                        title: data?.title || "",
                        totalApplicants: data?.totalApplicants,
                        updatedAt: data?.updatedAt,
                        walkInVenue: data?.walkInVenue &&
                          typeof data.walkInVenue == "object" && {
                            ...data.walkInVenue,
                          },
                      }}
                      pageName="SRP"
                      updateIsSaved={savedJobs.includes(data?.jobId)}
                      saveUnsaveJobHandler={() => {
                        saveUnsaveJobHandler(data?.jobId);
                      }}
                      className={`!min-h-[216px]`}
                      fullView={true}
                      applyBtnComponent={
                        <ApplyJob
                          jobData={data}
                          siteProperties={sitePropsProperties}
                          pageName={"Dashboard"}
                          btnClass={
                            "!h-auto md:!h-auto !px-2 !py-[6px] !text-[12px]"
                          }
                          isLoggedIn={true}
                          isMobile={isMobile}
                          isJobApplied={
                            userInfo?.appliedJobIds?.includes(data?.id || "") ||
                            false
                          }
                        />
                      }
                      siteContext={data?.siteContext || "rexmonster"}
                      userSkills={
                        data?.skills &&
                        Array.isArray(data.skills) && [
                          ...data.skills.map((data: any) => data?.text || ""),
                        ]
                      }
                    />
                  </div>
                  {!isMobile ? (
                    <div className={`${loader ? "" : "hidden"}`}>
                      <Skeleton />
                    </div>
                  ) : null}
                </>
              );
            })
          ) : !jobDescLoader ? (
            <NoResults query={noResultData["similarJobs"]} />
          ) : null}
        </div>

        {!isMobile && maxPageNum ? (
          <div className="flex justify-center">
            <div className="flex justify-center">
              <Pagination
                activePage={page}
                maxPageNum={maxPageNum}
                changeHandler={({ currPageNo, type }: any) => {
                  setLoader(true);
                  setPage((prev) =>
                    type == "leftArrow"
                      ? prev - 1
                      : type == "rightArrow"
                        ? prev + 1
                        : currPageNo,
                  );
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(SimilarJobs);
